(ns io.daskul.webapp.widgets.form.inline-table
  "Inline table widget for has-many relationships.

   Renders editable table with:
   - Inline cell editing
   - Add/remove row actions
   - Optional picker integration for pre-populating rows"
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.form.editable-cell :refer [editable-cell]]
   [io.daskul.webapp.widgets.form.picker-modal :refer [picker-modal]]))

(defui inline-table-widget
  "Inline table widget for editing array fields.

   Props:
   - field-key: Keyword - field name (e.g., :line-items)
   - value: Vector - current array value
   - set-field!: (fn [new-array]) - updates the field
   - form-data: Map - parent form state (for picker lookups)
   - ui: Map - UI configuration with :label, :columns, :picker, :min-rows, :max-rows
   - relationship: Map - relationship definition from aggregate (optional)"
  [{:keys [field-key value set-field! form-data ui relationship]}]
  (let [items (or value [])
        {:keys [label columns picker min-rows max-rows]} ui

        ;; Row operations - all update via set-field!
        add-row!
        (fn []
          (let [empty-row (zipmap (map :key columns) (repeat nil))]
            (set-field! (conj items empty-row))))

        remove-row!
        (fn [idx]
          (when (> (count items) (or min-rows 0))
            (set-field! (vec (concat (subvec (vec items) 0 idx)
                                     (subvec (vec items) (inc idx)))))))

        update-cell!
        (fn [idx col-key new-val]
          (set-field! (assoc-in (vec items) [idx col-key] new-val)))

        add-from-picker!
        (fn [picker-item]
          ;; Items from picker are already in child aggregate format
          (set-field! (conj items picker-item)))

        can-add? (or (nil? max-rows) (< (count items) max-rows))
        can-remove? (> (count items) (or min-rows 0))

        ;; Picker modal state
        [picker-open? set-picker-open!] (uix/use-state false)]

    ($ :div.inline-table-widget
      ;; Header
      ($ ui/group
        {:justify "space-between"
         :mb "sm"}
        ($ ui/text {:fw 600 :size "sm"} label)
        (when (and can-add? (not picker))
          ($ ui/button
            {:size "xs"
             :variant "light"
             :onClick add-row!}
            "+ Add")))

      ;; Table (scroll-area prevents horizontal overflow on mobile)
      ($ ui/scroll-area
        {:type "auto"}
        ($ ui/table
          {:striped true
           :withTableBorder true
           :withColumnBorders true
           :fontSize "sm"
           :style {:minWidth 400}}
        ($ ui/thead
          ($ ui/tr
            (for [{:keys [key label width]} columns]
              ($ ui/th
                {:key (name key)
                 :style {:width width}}
                label))
            ($ ui/th {:key "actions" :style {:width "50px"}} "")))

        ($ ui/tbody
          (for [[idx item] (map-indexed vector items)]
            ($ ui/tr
              {:key idx}
              ;; Column cells
              (for [{:keys [key editable input-type computed] :as col} columns]
                ($ ui/td
                  {:key (name key)}
                  (if (false? editable)
                    ;; Read-only / computed cell
                    ($ :span.readonly
                      {:style {:color "#666"}}
                      (if computed
                        (computed item)
                        (get item key)))
                    ;; Editable cell
                    ($ editable-cell
                      {:value (get item key)
                       :input-type (or input-type :text)
                       :on-change #(update-cell! idx key %)}))))

              ;; Row actions
              ($ ui/td
                {:key "actions"}
                (when can-remove?
                  ($ ui/action-icon
                    {:variant "subtle"
                     :color "red"
                     :size "sm"
                     :onClick #(remove-row! idx)}
                    ($ icons/icon-trash {:size 14})))))))))

      ;; Footer with add/picker buttons
      ($ ui/group
        {:gap "sm"
         :mt "sm"}
        (when can-add?
          ($ ui/button
            {:size "xs"
             :variant "default"
             :onClick add-row!}
            "+ Add Row"))

        (when (and picker can-add?)
          ($ ui/button
            {:size "xs"
             :variant "light"
             :onClick #(set-picker-open! true)}
            (str "📋 " (or (:label picker) "Pick")))))

      ;; Picker modal
      (when (and picker picker-open?)
        ($ picker-modal
          {:config picker
           :form-data form-data
           :columns columns
           :on-select (fn [item]
                        (add-from-picker! item)
                        (set-picker-open! false))
           :on-close #(set-picker-open! false)})))))
